import React, { useMemo } from 'react';
import type { CraftingSlot, CraftingRecipe, Resource } from '../../types';
import { Button } from '../common/Button';
import { ItemIcon } from '../common/ItemIcon';
import { Lock, Zap, X } from 'lucide-react';

interface ActiveCraftingSlotProps {
    slot: CraftingSlot;
    activeRecipe: CraftingRecipe | null;
    assignedRecipe: CraftingRecipe | null;
    resources: Record<Resource, number>;
    isDragOver: boolean;
    onDragOver: (e: React.DragEvent, slotId: number) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent, slotId: number) => void;
    onStopAuto: (slotId: number) => void;
    onClaim: (slotId: number) => void;
}

export const ActiveCraftingSlot: React.FC<ActiveCraftingSlotProps> = ({
    slot,
    activeRecipe,
    assignedRecipe,
    resources,
    isDragOver,
    onDragOver,
    onDragLeave,
    onDrop,
    onStopAuto,
    onClaim,
}) => {
    // Determine the state of the slot
    const isLocked = !slot.unlocked;
    const isWorking = !!(activeRecipe && slot.activeCraft);
    const isWaiting = !isWorking && !!assignedRecipe;
    const isIdle = !isWorking && !isWaiting;

    // Calculate progress if working
    const progress = useMemo(() => {
        if (!isWorking || !slot.activeCraft) return 0;
        const elapsed = Date.now() - slot.activeCraft.startTime;
        return Math.min(100, (elapsed / slot.activeCraft.duration) * 100);
    }, [isWorking, slot.activeCraft, Date.now()]);

    const remainingSeconds = useMemo(() => {
        if (!isWorking || !slot.activeCraft) return 0;
        const remaining = Math.max(0, (slot.activeCraft.startTime + slot.activeCraft.duration - Date.now()) / 1000);
        return remaining;
    }, [isWorking, slot.activeCraft, Date.now()]);

    const isFinished = isWorking && remainingSeconds <= 0;

    const iconItem = useMemo(() => {
        const r = activeRecipe || assignedRecipe;
        if (!r) return null;
        return Object.keys(r.output)[0] as Resource;
    }, [activeRecipe, assignedRecipe]);

    // LOCKED STATE
    if (isLocked) {
        return (
            <div style={{
                position: 'relative',
                background: 'rgba(0,0,0,0.2)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                height: '140px', // Fixed height matching active slots
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-muted)',
                opacity: 0.7,
                overflow: 'hidden',
                boxSizing: 'border-box'
            }}>
                <Lock size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Locked Slot</span>
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)'
                }} />
            </div>
        );
    }

    return (
        <div
            onDragOver={(e) => onDragOver(e, slot.id)}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, slot.id)}
            style={{
                position: 'relative',
                background: isDragOver ? 'var(--color-bg-panel-hover)' : 'var(--color-bg-panel)',
                backgroundColor: 'rgba(15, 23, 42, 0.6)',
                border: `1px solid ${isDragOver ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: '8px',
                padding: '12px',
                transition: 'all 0.2s',
                boxShadow: isDragOver ? '0 0 0 2px rgba(139, 92, 246, 0.2)' : 'inset 0 1px 3px rgba(0,0,0,0.3)',
                height: '140px', // STRICT FIXED HEIGHT
                minHeight: '140px',
                maxHeight: '140px',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box'
            }}
        >
            {/* Header: Fixed Height 24px */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', height: '24px', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'var(--color-text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}>
                        Slot {slot.id + 1}
                    </span>

                    {assignedRecipe && (
                        <div style={{
                            fontSize: '0.7rem',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            fontWeight: 600,
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <Zap size={10} fill="currentColor" />
                            AUTO
                        </div>
                    )}
                </div>

                {assignedRecipe && (
                    <button
                        onClick={() => onStopAuto(slot.id)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--color-text-muted)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px',
                            borderRadius: '4px',
                            marginTop: '-4px',
                            marginRight: '-4px',
                            transition: 'all 0.1s'
                        }}
                        title="Stop Auto-Crafting"
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-danger)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-muted)'}
                    >
                        <X size={16} />
                    </button>
                )}
            </div>

            {/* Content Area - Flex 1 to fill remaining space */}
            {isIdle ? (
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    color: 'var(--color-text-muted)',
                    fontSize: '0.875rem',
                    background: isDragOver ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                    opacity: isDragOver ? 1 : 0.6,
                    height: '100%', // Fill the available space
                    boxSizing: 'border-box'
                }}>
                    {isDragOver ? (
                        <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Drop to assign</span>
                    ) : (
                        <span>Drag recipe here</span>
                    )}
                </div>
            ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>

                    {/* Top Part: Icon + Title */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', height: '40px' }}>
                        {iconItem && (
                            <div style={{
                                width: '40px',
                                height: '40px',
                                background: 'var(--color-bg-app)',
                                border: '1px solid var(--color-border)',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
                                flexShrink: 0
                            }}>
                                <ItemIcon id={iconItem} size={28} />
                            </div>
                        )}

                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{
                                fontWeight: 600,
                                color: 'var(--color-text-primary)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {(activeRecipe || assignedRecipe)?.name}
                            </div>

                            {/* Subtext */}
                            {!isWorking && isWaiting ? (
                                (() => {
                                    const missing = Object.entries(assignedRecipe!.input).filter(([res, amt]) =>
                                        (resources[res as Resource] ?? 0) < (amt as number)
                                    );
                                    if (missing.length > 0) {
                                        return (
                                            <div style={{ fontSize: '0.75rem', color: 'var(--color-danger)', display: 'flex', alignItems: 'center', gap: '4px', height: '18px', overflow: 'hidden' }}>
                                                Missing: {missing.map(([res]) => res.replace(/_/g, ' ')).join(', ')}
                                            </div>
                                        );
                                    }
                                    return <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Processing...</div>;
                                })()
                            ) : (
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-skill-crafting)' }}>
                                    {isFinished ? 'Ready to Claim' : 'Crafting...'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Part: Progress + Action Button/Timer */}
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '8px' }}>

                        {/* Progress Track: Always reserve space or conditionally show? 
                             To be perfectly stable, we could always show it but invisible. 
                             But 'gap: 8px' handles separation. Let's trust flex.
                             If we want max stability, let's wrap it.
                         */}
                        <div style={{ height: '6px', width: '100%' }}>
                            {isWorking && (
                                <div style={{
                                    height: '100%',
                                    background: 'rgba(0,0,0,0.3)',
                                    borderRadius: '3px',
                                    overflow: 'hidden',
                                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
                                }}>
                                    <div style={{
                                        height: '100%',
                                        background: isFinished ? 'var(--color-success)' : 'linear-gradient(90deg, var(--color-skill-crafting), #fbbf24)',
                                        width: `${progress}%`,
                                        transition: 'width 0.1s linear',
                                        boxShadow: '0 0 10px rgba(234, 179, 8, 0.3)'
                                    }} />
                                </div>
                            )}
                        </div>

                        {/* Action Area: Fixed Height 32px */}
                        <div style={{ height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isWorking ? (
                                isFinished ? (
                                    <Button
                                        fullWidth
                                        variant="success"
                                        onClick={() => onClaim(slot.id)}
                                        style={{
                                            animation: 'pulse 2s infinite',
                                            fontSize: '0.875rem',
                                            padding: '0 8px',
                                            height: '100%'
                                        }}
                                    >
                                        Claim Item
                                    </Button>
                                ) : (
                                    <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '0.75rem',
                                        color: 'var(--color-text-muted)'
                                    }}>
                                        <span>{Math.floor(progress)}%</span>
                                        <span>{remainingSeconds.toFixed(1)}s</span>
                                    </div>
                                )
                            ) : (
                                /* Empty spacer for non-working state */
                                <div />
                            )}
                        </div>
                    </div>
                </div>
            )}
            <style>{`
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
                    70% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
                }
            `}</style>
        </div>
    );
};
